/**
 * Where the flow's progress bar goes, so it never covers the dialog the flow is
 * filling in.
 *
 * 🔴 THIS REPLACES A RULE THAT RESIZED THE HOST'S OWN DIALOGS, and the resize is
 * the bug it exists to undo. `tour-guide.css` used to carry
 *
 *     body.tour-task-active [role='dialog'][data-state='open'] {
 *       max-height: min(95vh, calc(100vh - 140px)) !important;
 *     }
 *
 * — a clamp on EVERY open dialog for as long as a flow ran, bought to keep the
 * bar off the dialog's own footer. What it actually did was change the product's
 * dimensions the moment a walkthrough started:
 *
 *   - the form builder is a `FloatingDrawer` — `top: 12px; bottom: 12px`, so its
 *     height is its two offsets. Clamping it to `100vh - 140px` left the panel
 *     anchored at the top with its bottom edge floating ~116px above where it
 *     belongs: a visibly shorter drawer than the one the same button opens with
 *     no tour running,
 *   - the template composer is `max-h-[95vh]` with a `sticky` preview column
 *     sized `max-h-[calc(95vh-13rem)]` INSIDE it. The clamp moved the outer box
 *     and not the inner one, so the preview ran past the dialog it lives in,
 *   - and a plain `DialogContent` has no scroll container of its own — a
 *     max-height it was never designed for simply cuts its content off.
 *
 * So the dialog is left exactly as the app draws it, and the BAR moves instead:
 * it stays docked to the bottom, and when the dialog reaches down into the strip
 * it sits in, it steps sideways into whichever gutter beside the dialog has more
 * room, narrowing (and dropping its title and its inactive step labels) to fit.
 *
 * 🔴 GEOMETRY ONLY, AND NEVER THE BAR'S OWN MEASURED WIDTH. Every number below
 * comes from the dialog and the viewport. Deciding "does the bar overlap?" from
 * the bar's rendered width would feed its own outcome: a wide bar dodges into a
 * gutter, the gutter narrows it, the narrow bar no longer overlaps, it moves
 * back to the middle, and it is wide again — a pill flickering between two
 * positions for as long as the dialog is open. `NOMINAL_WIDTH` is the width the
 * overlap test assumes precisely so that the answer cannot depend on the answer.
 */

import type { TaskTargetRect } from '../engine/TaskEngine';

/**
 * Kept off the edge of the screen. Matches `.tour-task-bar { bottom }`, so the
 * bar keeps the same inset whichever edge it is measured from.
 */
const MARGIN = 12;

/** Kept off the dialog itself, so the two never look joined. */
const GAP = 12;

/**
 * The strip along the bottom of the screen the bar occupies.
 *
 * A constant rather than the pill's measured height, for the reason in the file
 * comment. Sized for the running pill (~50px) plus a little; the finished one
 * wraps taller, and `FOOTER_REACH` below is the slack that covers it.
 */
const BAR_BAND = 56;

/**
 * How far into that strip a dialog has to reach before the bar moves.
 *
 * 🔴 WITHOUT THIS THE BAR DODGES FOR DIALOGS IT IS NOT COVERING. A centred
 * `max-h-[85vh]` dialog — the shape most of this app's dialogs are — ends within
 * a few pixels of the pill on a laptop, and the pill's own rounded corner over
 * the dialog's rounded corner hides nothing: a footer keeps its controls a good
 * ~24px inside its bottom edge. Dodging on that overlap would push the bar into
 * a gutter, and drop its title and its labels to fit one, on nearly every step
 * of every flow — motion and lost words paid for nothing. A dialog that reaches
 * THIS far in is one whose buttons are genuinely underneath.
 */
const FOOTER_REACH = 24;

/**
 * The width the overlap test assumes for the centred bar.
 *
 * Only ever used to ask WHETHER the dialog is in the way — never to place
 * anything. A flow of five or six steps renders at roughly this width with its
 * title and labels showing.
 */
const NOMINAL_WIDTH = 420;

/**
 * Narrowest the bar is ever squeezed to.
 *
 * At this width it is the step count and the exit × with the chips scrolling
 * between them (`.tour-task-bar__list` is the bar's own scrollport), which is
 * the least that still says how far in you are and still lets you leave.
 */
const MIN_WIDTH = 96;

/**
 * Below this the bar drops its title and the labels of every step except the
 * one being worked on — the same trade the `max-width: 767px` rules already
 * make, applied to a narrow GUTTER rather than a narrow screen.
 */
const COMPACT_WIDTH = 340;

/**
 * How far above the viewport's bottom the bar will lift itself, at most.
 *
 * The cap is what keeps the rule proportionate. A short control in the strip —
 * the case this exists for — lifts the bar by a few dozen pixels and nothing
 * else changes. A target that is TALL and reaches the bottom (a whole panel, a
 * preview column) would otherwise send the pill halfway up the screen to clear a
 * box whose top edge is nowhere near the bar; there the overlap is the bottom
 * sliver of a large ring, which is not what "the highlight is hidden" means.
 */
const MAX_LIFT = 220;

/**
 * Lift the bar clear of the step's own target, when the target is underneath it.
 *
 * 🔴 THIS IS "IT HIDES THE CONTENT AT SOME STEPS", AND IT IS ABOUT THE TARGET,
 * NOT THE DIALOG. `placeTaskBar` deliberately stopped dodging the dialog — read
 * the block comment below before touching that — and named the cost: on a dialog
 * whose footer reaches into the bottom strip, the pill sits over that footer.
 * That cost came due on the campaigns walkthrough's "Create and send" step, whose
 * target IS the drawer's footer button: the callout read "Waiting for you to
 * press the send button…" while the bar covered the button being waited on.
 *
 * So the rule is drawn as narrowly as the complaint: the bar moves only when it
 * is over the ONE control the step is pointing at, and it moves straight UP —
 * staying centred, which is what the dodge was stood down for. Everything else
 * (a dialog footer with no step target in it, a target anywhere above the strip)
 * is left exactly where it was.
 *
 * 🔴 NO FEEDBACK LOOP, WHICH IS WHY THIS ONE MAY MEASURE THE FULL BAND. The
 * file comment's warning is about the bar's own WIDTH deciding whether the bar
 * moves sideways — a dodge that narrows the pill until it no longer needs to
 * dodge. A lift changes neither the bar's width nor the target's box, so
 * assuming the bar spans the whole strip is safe here. It is also the honest
 * assumption: `maxWidth` is the full viewport now, and a long flow really does
 * render that wide.
 */
export function liftFor(target: TaskTargetRect | null, viewport: BarViewport): number {
  if (!target) return 0;

  // The top edge of the strip the bar occupies when it is sitting normally.
  const bandTop = viewport.height - MARGIN - BAR_BAND;
  const targetBottom = target.top + target.height;

  // Clear of the strip already — the overwhelming majority of steps.
  if (targetBottom <= bandTop) return 0;

  // Below the fold entirely: the engine scrolls a target into view before
  // ringing it, so this is a rect measured mid-scroll. Lifting for it would
  // bounce the bar for one tick and put it back.
  if (target.top >= viewport.height) return 0;

  /**
   * 🔴 MEASURED FROM THE BAR'S BOTTOM EDGE, NOT FROM THE TOP OF ITS BAND. The
   * bar is laid out with `bottom: MARGIN + lift` and grows upward from there, so
   * the lowest pixel it paints is at `height - MARGIN - lift`. Clearing the
   * target means putting THAT above the target's top edge. Solving against the
   * band's top instead under-lifts by the whole height of the band — a 4px hop
   * for a footer button, which leaves the pill exactly where it was.
   */
  const needed = viewport.height - MARGIN - target.top + GAP;
  return Math.max(0, Math.min(MAX_LIFT, Math.round(needed)));
}

/** Which edge the bar is docked to, or the middle when nothing is in the way. */
export type BarDodge = 'none' | 'left' | 'right';

export interface BarPlacement {
  dodge: BarDodge;
  /** Distance from that edge of the viewport. Unused when `dodge` is 'none'. */
  inset: number;
  /** Cap on the bar's width. */
  maxWidth: number;
  /** Drop the title and the inactive step labels. */
  compact: boolean;
  /**
   * Extra pixels ABOVE the bar's usual bottom inset, so it clears the step's own
   * highlighted target. Zero on nearly every step; see `liftFor`.
   */
  lift: number;
}

export interface BarViewport {
  width: number;
  height: number;
}

/**
 * Place the bar for the dialog currently holding the step's target.
 *
 * `avoid` is `TaskRunState.avoid` — the dialog the target sits in, or null on a
 * sidebar/page step.
 *
 * ┌─ THE BAR IS ALWAYS CENTRED. IT NO LONGER DODGES. ──────────────────────────┐
 * │ 🔴 THIS IS A PRODUCT DECISION, NOT A SIMPLIFICATION, AND IT REVERSES THE   │
 * │ ONE THIS MODULE WAS BUILT FOR — so read the whole file comment above before│
 * │ changing it back. The dodge stepped the pill into whichever gutter beside  │
 * │ the dialog had more room. It worked, and it was reported as a bug twice    │
 * │ over: the bar arrives docked hard against the left edge of the screen on   │
 * │ every drawer-based flow (the campaign drawer and the form builder are both │
 * │ tall enough to trigger it), which reads as the bar having come loose — and │
 * │ on the form builder the walkthrough's own progress is then the furthest    │
 * │ thing on screen from the form being filled in.                            │
 * │                                                                           │
 * │ Centred and LAYERED OVER the dialog is what was asked for instead. That is │
 * │ free: `.tour-task-bar` sits at `z-index: 2147482400` and this app's        │
 * │ drawers and dialogs are `z-50`, so the pill already paints on top of them  │
 * │ — the dodge was the only thing that ever moved it away.                    │
 * │                                                                           │
 * │ WHAT THIS GIVES BACK UP, honestly: on a dialog whose footer reaches into   │
 * │ the bottom strip, the pill sits over that footer. It is ~50px tall, docked │
 * │ bottom-centre, and a footer keeps its controls inset from its own edge, so │
 * │ the usual cost is a rounded corner over a rounded corner. Where it does    │
 * │ overlap a real control, the bar's own × closes it. Restoring the resize    │
 * │ rule the file comment describes is NOT the alternative — that changed the  │
 * │ product's own dimensions and is the bug this module exists to have undone. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The signature keeps both arguments: the decision is deliberately still made in
 * one place from geometry, so re-introducing a narrower rule (dodge only for a
 * dialog that genuinely buries the pill, say) is a change to this function and
 * nothing else. Everything it used to compute is still measured in the constants
 * above, which is why they are kept rather than deleted.
 */
export function placeTaskBar(
  avoid: TaskTargetRect | null,
  viewport: BarViewport,
  /**
   * The step's own highlighted target — `TaskRunState.target`. Optional so the
   * arithmetic stays callable with the dialog alone, which is all the placement
   * itself needs; only the lift consults it.
   */
  target: TaskTargetRect | null = null,
): BarPlacement {
  // Referenced so the parameter stays part of the contract while the rule that
  // used it is stood down — and so the next reader sees exactly where a narrower
  // dodge would hook back in.
  void avoid;

  const full = Math.max(MIN_WIDTH, viewport.width - MARGIN * 2);
  return {
    dodge: 'none',
    inset: MARGIN,
    maxWidth: full,
    compact: false,
    // 🔴 THE ONE THING THAT CAN STILL MOVE THE BAR. Not the dialog — the step's
    // own target, and only when the bar is sitting on top of it. See `liftFor`.
    lift: liftFor(target, viewport),
  };
}
